import { defineStore } from 'pinia';
import { FRACTIONATION_PARAM_NAMES, validateFractionationInput } from '@/composables/calculationConstants';
import { sumTotalAbsorbedDose } from '@/utils/fractionationDose';
import {
  splitTcpParameters,
  splitNtcpParameters,
  NIEMIERKO_PER_STAGE_PARAM,
} from '@/utils/adaptiveRtNiemierkoParams';
import { createAdaptiveRtSlotId } from '@/utils/adaptiveRtStageId';
import {
  aliasGroupsToApiPayload,
  normalizeAliasGroupsFromApi,
} from '@/utils/adaptiveRtStructureAliases';

function emptyFractionation() {
  return {
    total_absorbed_dose: null,
    dose_per_fraction: null,
    number_of_fractions: null,
  };
}

/** Минимальное число карточек этапов на экране загрузки (нельзя удалить блок). */
export const ADAPTIVE_RT_MIN_STAGE_SLOTS = 2;

export function createEmptyStage() {
  return {
    slotId: createAdaptiveRtSlotId(),
    planId: null,
    displayName: '',
    fractionation: emptyFractionation(),
    structureCount: null,
    parameters: [],
    paramsLoaded: false,
    ntcpParameters: [],
    ntcpParamsLoaded: false,
  };
}

export function localStageStatus(stage) {
  if (!stage?.planId || stage.planMissing) return 'missing_dvh';
  const check = validateFractionationInput(stage.fractionation);
  if (!check.ok) return 'missing_dose';
  return 'ready';
}

const STAGE_STATUS_LABELS = {
  missing_dvh: 'Нет ГДО',
  missing_dose: 'Нет дозы',
  ready: 'Готов',
};
export function stageStatusLabel(status) {
  return STAGE_STATUS_LABELS[status] || status;
}

function ensurePerStageA(perStage) {
  if (perStage.some((p) => p.name === NIEMIERKO_PER_STAGE_PARAM)) {
    return perStage;
  }
  return [
    ...perStage,
    {
      name: NIEMIERKO_PER_STAGE_PARAM,
      value: '',
      description: '',
    },
  ];
}

function mapStageParamsFromHistory(s, paramKey) {
  const frac = s.fractionation || {};
  const modelEntries = Object.entries(s.params || {}).filter(
    ([name]) => name && !FRACTIONATION_PARAM_NAMES.includes(name)
  );
  return {
    slotId: s.slot_id || createAdaptiveRtSlotId(),
    planId: s.plan_id,
    displayName: (s.display_name || '').trim() || s.plan_id,
    fractionation: {
      total_absorbed_dose: frac.total_absorbed_dose ?? frac.totalDose ?? null,
      dose_per_fraction: frac.dose_per_fraction ?? null,
      number_of_fractions: frac.number_of_fractions ?? frac.n_f ?? null,
    },
    parameters: paramKey === 'parameters'
      ? modelEntries.map(([name, value]) => ({
          name,
          value: String(value),
          description: '',
        }))
      : [],
    paramsLoaded: paramKey === 'parameters' && modelEntries.length > 0,
    ntcpParameters: paramKey === 'ntcpParameters'
      ? modelEntries.map(([name, value]) => ({
          name,
          value: String(value),
          description: '',
        }))
      : [],
    ntcpParamsLoaded: paramKey === 'ntcpParameters' && modelEntries.length > 0,
  };
}

export const useAdaptiveRtStore = defineStore('adaptiveRt', {
  state: () => ({
    stages: [],
    /** TCD50, γ50, α/β — общие для TCP (Niemierko). */
    sharedTcpParameters: [],
    /** TD50, γ50, α/β — общие для NTCP (Niemierko). */
    sharedNtcpParameters: [],
    commonStructures: [],
    selectedStructure: null,
    selectedNtcpStructure: null,
    validationWarnings: [],
    wizardValidation: null,
    wizardAckExcluded: false,
    /** Ручные алиасы имён структур между этапами. */
    structureAliasGroups: [],
    /** Этапы или параметры изменены после последнего расчёта */
    calculationStale: false,
  }),
  getters: {
    planIds(state) {
      return state.stages.map((s) => s.planId).filter(Boolean);
    },
    hasMinimumStages(state) {
      return state.stages.filter((s) => s.planId).length >= ADAPTIVE_RT_MIN_STAGE_SLOTS;
    },
    /** Σ(d×nf) по всем этапам */
    totalAbsorbedDoseAllStages(state) {
      return sumTotalAbsorbedDose(state.stages.map((s) => s.fractionation));
    },
    canRunWizardValidate(state) {
      if (state.stages.length < ADAPTIVE_RT_MIN_STAGE_SLOTS) return false;
      return state.stages.every((s) => {
        if (!s.planId) return false;
        return validateFractionationInput(s.fractionation).ok;
      });
    },
  },
  actions: {
    setStages(stages) {
      this.stages = stages;
      this.ensureStageSlotIds();
      this.clearWizardValidation();
    },
    ensureStageSlotIds() {
      this.stages.forEach((s) => {
        if (!s.slotId) {
          s.slotId = createAdaptiveRtSlotId();
        }
      });
    },
    addStage() {
      this.stages.push(createEmptyStage());
    },
    clearStage(index) {
      if (!this.stages[index]) return;
      this.stages[index] = createEmptyStage();
      this.resetStageModelParams();
      this.clearWizardValidation();
    },
    removeStage(index) {
      if (index < ADAPTIVE_RT_MIN_STAGE_SLOTS) {
        this.clearStage(index);
        return;
      }
      if (this.stages.length <= ADAPTIVE_RT_MIN_STAGE_SLOTS) return;
      this.stages = this.stages.filter((_, i) => i !== index);
      this.resetStageModelParams();
      this.clearWizardValidation();
    },
    reorderStage(fromIndex, toIndex) {
      if (fromIndex === toIndex) return;
      if (fromIndex < 0 || toIndex < 0) return;
      if (fromIndex >= this.stages.length || toIndex >= this.stages.length) return;
      const next = [...this.stages];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      this.stages = next;
      this.resetStageModelParams();
      this.clearWizardValidation();
    },
    setStagePlan(index, { planId, displayName }) {
      if (!this.stages[index]) return;
      this.stages[index].planId = planId;
      this.stages[index].planMissing = false;
      this.stages[index].displayName = displayName || planId;
      this.stages[index].paramsLoaded = false;
      this.stages[index].parameters = [];
      if (index === 0) {
        this.sharedTcpParameters = [];
        this.sharedNtcpParameters = [];
      }
      this.resetStageModelParams();
      this.clearWizardValidation();
    },
    setStageFractionation(index, fractionation) {
      if (!this.stages[index]) return;
      this.stages[index].fractionation = { ...emptyFractionation(), ...fractionation };
      this.clearWizardGate();
    },
    /** Сброс параметров модели (без баннера «этапы изменены») — загрузка/мастер этапов. */
    resetStageModelParams() {
      this.stages.forEach((s) => {
        s.paramsLoaded = false;
        s.parameters = [];
        s.ntcpParamsLoaded = false;
        s.ntcpParameters = [];
      });
      this.sharedTcpParameters = [];
      this.sharedNtcpParameters = [];
      this.commonStructures = [];
    },
    /** После расчёта: этапы изменились — показать баннер и сбросить параметры модели. */
    invalidateCalculationState() {
      this.calculationStale = true;
      this.resetStageModelParams();
    },
    clearCalculationStale() {
      this.calculationStale = false;
    },
    /** Сброс gate без удаления ручных алиасов (доза этапа изменилась). */
    clearWizardGate() {
      this.wizardValidation = null;
      this.wizardAckExcluded = false;
    },
    clearWizardValidation({ clearAliases = true } = {}) {
      this.clearWizardGate();
      if (clearAliases) {
        this.structureAliasGroups = [];
      }
    },
    setStructureAliasGroups(groups) {
      this.structureAliasGroups = groups || [];
    },
    applyWizardValidation(payload) {
      this.wizardValidation = payload || null;
      this.commonStructures = payload?.common_structures || [];
      this.validationWarnings = payload?.warnings || [];
      if (Array.isArray(payload?.structure_aliases)) {
        this.structureAliasGroups = normalizeAliasGroupsFromApi(payload.structure_aliases);
      }
      if (payload?.stages?.length) {
        payload.stages.forEach((srv) => {
          const idx = this.stages.findIndex((_, i) => i + 1 === srv.order);
          if (idx < 0) return;
          this.stages[idx].structureCount = srv.structure_count;
          this.stages[idx].planMissing = Array.isArray(srv.issues)
            && srv.issues.includes('plan_not_found');
        });
      }
    },
    buildValidatePayload() {
      const payload = {
        stages: this.stages.map((s, index) => ({
          order: index + 1,
          plan_id: s.planId || '',
          fractionation: { ...s.fractionation },
        })),
      };
      const aliases = aliasGroupsToApiPayload(this.structureAliasGroups);
      if (aliases.length) {
        payload.structure_aliases = aliases;
      }
      return payload;
    },
    setSharedTcpParameters(parameters) {
      this.sharedTcpParameters = parameters || [];
    },
    setSharedNtcpParameters(parameters) {
      this.sharedNtcpParameters = parameters || [];
    },
    applyTcpParametersFromModel(index, parameters) {
      if (!this.stages[index]) return;
      const { shared, perStage } = splitTcpParameters(parameters);
      if (index === 0 && shared.length) {
        this.sharedTcpParameters = shared;
      }
      this.stages[index].parameters = ensurePerStageA(perStage);
      this.stages[index].paramsLoaded = true;
    },
    applyNtcpParametersFromModel(index, parameters) {
      if (!this.stages[index]) return;
      const { shared, perStage } = splitNtcpParameters(parameters);
      if (index === 0 && shared.length) {
        this.sharedNtcpParameters = shared;
      }
      this.stages[index].ntcpParameters = ensurePerStageA(perStage);
      this.stages[index].ntcpParamsLoaded = true;
    },
    /** @deprecated use applyTcpParametersFromModel */
    setStageParameters(index, parameters) {
      this.applyTcpParametersFromModel(index, parameters);
    },
    /** @deprecated use applyNtcpParametersFromModel */
    setStageNtcpParameters(index, parameters) {
      this.applyNtcpParametersFromModel(index, parameters);
    },
    setSelectedNtcpStructure(name) {
      this.selectedNtcpStructure = name;
    },
    setCommonStructures(structures) {
      this.commonStructures = structures || [];
      if (this.selectedStructure && !this.commonStructures.includes(this.selectedStructure)) {
        this.selectedStructure = null;
      }
    },
    setSelectedStructure(name) {
      this.selectedStructure = name;
    },
    setValidationWarnings(warnings) {
      this.validationWarnings = warnings || [];
    },
    clear() {
      this.stages = [];
      this.sharedTcpParameters = [];
      this.sharedNtcpParameters = [];
      this.commonStructures = [];
      this.selectedStructure = null;
      this.selectedNtcpStructure = null;
      this.validationWarnings = [];
      this.wizardValidation = null;
      this.wizardAckExcluded = false;
      this.structureAliasGroups = [];
      this.calculationStale = false;
    },
    _applySharedFromHistoryStages(mapped, paramKey) {
      const firstList =
        paramKey === 'parameters'
          ? mapped[0]?.parameters
          : mapped[0]?.ntcpParameters;
      if (!firstList?.length) return;
      const splitFn = paramKey === 'parameters' ? splitTcpParameters : splitNtcpParameters;
      const { shared } = splitFn(firstList);
      if (paramKey === 'parameters') {
        this.sharedTcpParameters = shared;
      } else {
        this.sharedNtcpParameters = shared;
      }
      mapped.forEach((src, i) => {
        if (!this.stages[i]) return;
        const list =
          paramKey === 'parameters' ? src.parameters : src.ntcpParameters;
        const { perStage } = splitFn(list);
        if (paramKey === 'parameters') {
          this.stages[i].parameters = ensurePerStageA(perStage);
          this.stages[i].paramsLoaded = src.paramsLoaded;
        } else {
          this.stages[i].ntcpParameters = ensurePerStageA(perStage);
          this.stages[i].ntcpParamsLoaded = src.ntcpParamsLoaded;
        }
      });
    },
    restoreTcpFromHistory(params) {
      if (!params || !Array.isArray(params.stages)) return false;
      const mapped = params.stages.map((s) => mapStageParamsFromHistory(s, 'parameters'));
      if (!this.stages.length) {
        this.stages = mapped;
      } else {
        mapped.forEach((src, i) => {
          if (!this.stages[i]) return;
          this.stages[i].paramsLoaded = src.paramsLoaded;
          if (!this.stages[i].planId) {
            this.stages[i].planId = src.planId;
            this.stages[i].displayName = src.displayName;
          }
          const currentFractionationOk = validateFractionationInput(
            this.stages[i].fractionation
          ).ok;
          if (!currentFractionationOk && src.fractionation.total_absorbed_dose != null) {
            this.stages[i].fractionation = { ...src.fractionation };
          }
        });
      }
      this._applySharedFromHistoryStages(mapped, 'parameters');
      this.selectedStructure = params.structure_name || null;
      if (Array.isArray(params.structure_aliases) && params.structure_aliases.length) {
        this.structureAliasGroups = normalizeAliasGroupsFromApi(params.structure_aliases);
      }
      return true;
    },
    restoreNtcpFromHistory(params) {
      if (!params || !Array.isArray(params.stages)) return false;
      const mapped = params.stages.map((s) => mapStageParamsFromHistory(s, 'ntcpParameters'));
      if (!this.stages.length) {
        this.stages = mapped;
      } else {
        mapped.forEach((src, i) => {
          if (!this.stages[i]) return;
          this.stages[i].ntcpParamsLoaded = src.ntcpParamsLoaded;
        });
      }
      this._applySharedFromHistoryStages(mapped, 'ntcpParameters');
      this.selectedNtcpStructure = params.structure_name || null;
      if (Array.isArray(params.structure_aliases) && params.structure_aliases.length) {
        this.structureAliasGroups = normalizeAliasGroupsFromApi(params.structure_aliases);
      }
      return true;
    },
    /** @deprecated use restoreTcpFromHistory */
    restoreFromHistory(params) {
      return this.restoreTcpFromHistory(params);
    },
  },
});
